'use client'

import { Chip as HeroChip, ChipProps as HeroChipProps } from '@heroui/react'
import { forwardRef } from 'react'
import { Icon } from '@/components/ui/icon'
import type { IconName } from '@/config/icons'

export interface ChipProps extends HeroChipProps {
  /** Icon to display at the start */
  startIcon?: IconName
  /** Icon to display at the end */
  endIcon?: IconName
  /** Close icon (when closeable) */
  closeIcon?: IconName
  /** Click handler for the chip */
  onClick?: (e: React.MouseEvent) => void
  /** Standard HTML attributes */
  'data-testid'?: string
}

export const Chip = forwardRef<HTMLDivElement, ChipProps>(
  ({ startIcon, endIcon, closeIcon = 'close', onClick, onClose, startContent, endContent, children, ...props }, ref) => {
    // Handle click event if provided
    const handleClick = onClick ? (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      onClick(e)
    } : undefined

    const finalStartContent = startIcon ? (
      <Icon icon={startIcon} className="w-4 h-4" />
    ) : startContent

    const finalEndContent = endIcon ? (
      <Icon icon={endIcon} className="w-4 h-4" />
    ) : endContent

    return (
      <HeroChip
        ref={ref}
        startContent={finalStartContent}
        endContent={finalEndContent}
        onClick={handleClick}
        className={onClick ? 'cursor-pointer' : undefined}
        {...props}
      >
        {children}
        {onClose && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
            aria-label="Remove"
            className="-mr-1 ml-1 inline-flex items-center justify-center rounded-full p-0.5 text-default-500 transition-colors hover:bg-default-200 hover:text-foreground"
          >
            <Icon icon={closeIcon} className="text-[14px]" />
          </button>
        )}
      </HeroChip>
    )
  }
)

Chip.displayName = 'Chip'

// Status chip variants for common use cases
export interface StatusChipProps extends Omit<ChipProps, 'color' | 'variant'> {
  status: 'success' | 'warning' | 'danger' | 'info' | 'neutral'
  variant?: 'solid' | 'bordered' | 'light' | 'flat' | 'faded' | 'shadow' | 'dot'
}

export const StatusChip = forwardRef<HTMLDivElement, StatusChipProps>(
  ({ status, variant = 'flat', ...props }, ref) => {
    const colorMap = {
      success: 'success' as const,
      warning: 'warning' as const,
      danger: 'danger' as const,
      info: 'primary' as const,
      neutral: 'default' as const,
    }

    return (
      <Chip
        ref={ref}
        color={colorMap[status]}
        variant={variant}
        {...props}
      />
    )
  }
)

StatusChip.displayName = 'StatusChip'

// Interactive chip for filters, tags, etc.
export interface InteractiveChipProps extends ChipProps {
  /** Whether the chip is selected */
  selected?: boolean
  /** Handler for selection change */
  onSelectionChange?: (selected: boolean) => void
  /** Selection variant styling */
  selectionVariant?: 'toggle' | 'radio'
}

export const InteractiveChip = forwardRef<HTMLDivElement, InteractiveChipProps>(
  ({ selected = false, onSelectionChange, selectionVariant = 'toggle', onClick, color, variant, ...props }, ref) => {
    const handleClick = (e: React.MouseEvent) => {
      if (onSelectionChange) {
        onSelectionChange(selectionVariant === 'toggle' ? !selected : true)
      }
      onClick?.(e)
    }

    const chipColor = selected ? (color || 'primary') : 'default'
    const chipVariant = selected ? (variant || 'solid') : (variant || 'bordered')

    return (
      <Chip
        ref={ref}
        color={chipColor}
        variant={chipVariant}
        onClick={handleClick}
        {...props}
      />
    )
  }
)

InteractiveChip.displayName = 'InteractiveChip'

export default Chip